// Shared renderers for the two time questions that show up all over the app:
//
//   "how much longer will this take?"   → <EstimateCell>
//   "how long has this been sitting?"   → <TimeSince>
//
// Both are deliberately honest about gaps. An estimate built on missing history
// is shown as a gap, never as a smaller number, because a confident-looking
// underestimate is worse than an admitted unknown.
import dayjs from 'dayjs';

export const fmtEta = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const SETUP_LABEL = {
  COLOR_CHANGE: 'colour change',
  PRODUCT_CHANGEOVER: 'product changeover',
  PHASE_CHANGEOVER: 'phase changeover',
  DRUM_CHANGE: 'drum change'
};

// Where the number came from. An operator's own record and a figure borrowed from
// the machine are not the same claim and must not read the same.
const BASIS_TITLE = {
  operator: "from this operator's history",
  machine: 'machine median',
  global: 'overall median'
};

export const EstimateCell = ({ est, compact = false }) => {
  if (!est) return <span className="text-700">—</span>;

  const noRun = est.runSec == null;
  const noSetup = est.setupReason && est.setupSec == null;

  return (
    <div className="d-flex flex-column">
      <span className={`fw-semibold ${noRun ? 'text-warning' : ''}`}>
        {est.totalSec > 0 ? `~${fmtEta(est.totalSec)}` : noRun ? 'no data' : '—'}
      </span>

      {!compact && est.setupSec > 0 ? (
        <span
          className="fs-10 text-700 fw-normal"
          title={`${SETUP_LABEL[est.setupReason] || est.setupReason} · ${BASIS_TITLE[est.basis] || 'estimated'}`}
        >
          +{fmtEta(est.setupSec)} setup
        </span>
      ) : null}

      {!compact && noRun ? (
        <span className="fs-10 text-warning fw-normal" title="No speed history for this product">
          no speed history
        </span>
      ) : null}

      {!compact && noSetup ? (
        <span className="fs-10 text-warning fw-normal" title="No history for this kind of setup">
          setup not measured
        </span>
      ) : null}
    </div>
  );
};

/**
 * How long ago something happened — for "finished at / available since".
 * Colours by age so a drum that has been waiting in production for two days is
 * visible without reading the date.
 */
export const TimeSince = ({ ts, label = null, warnHours = 24, dangerHours = 72 }) => {
  const t = Number(ts);
  if (!Number.isFinite(t) || t <= 0) return <span className="text-700">—</span>;

  const d = dayjs(t);
  const hours = dayjs().diff(d, 'hour', true);
  const cls = hours >= dangerHours ? 'text-danger' : hours >= warnHours ? 'text-warning' : 'text-700';

  const ago =
    hours < 1
      ? `${Math.max(1, Math.round(hours * 60))}m ago`
      : hours < 48
        ? `${Math.round(hours)}h ago`
        : `${Math.round(hours / 24)}d ago`;

  return (
    <div className="d-flex flex-column">
      <span className={`fw-semibold ${cls}`} title={d.format('DD MMM YYYY, HH:mm')}>
        {ago}
      </span>
      <span className="fs-10 text-700 fw-normal">
        {label ? `${label} ` : ''}
        {d.format('DD MMM, HH:mm')}
      </span>
    </div>
  );
};

export default EstimateCell;
